import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { ProductModel } from '~/shared/models/system/ProductModel';
import {
  ReservoirModel,
  SystemModel,
} from '~/shared/models/system/SystemModel';
import { assertIsNonBlankString } from '~/types/assert-type';

function dateToShortIsoString(date: Date) {
  return date.toISOString().replace('.000Z', 'Z');
}

export const createRoutes = {
  Base: '/',
  AdviceReport: '/systems/:systemId/concept-report',
  Advanced: 'advanced',
  DynamicInfo: 'dynamic-info',
  Dashboard: '/systems/:systemId/dashboard/',
  Downloads: '/systems/:systemId/downloads/',
  Product: 'products/:productId',
  Reservoir: 'reservoirs/:reservoirId',
  Drain: 'drains/:drainId',
  ForgotPassword: '/forgot-password',
  ForgotPasswordConfirm: '/forgot-password-confirm',
  CreatePassword: '/create-password',
  Device: 'device/:deviceId',
  Metrics: '/systems/:systemId/metrics',
  NotFound: '*',
  Profile: '/profile',
  Issues: '/issues',
  SignIn: '/signin',
  Support: '/support',
  System: '/systems/:systemId',
  // TODO: Remove after transition period
  SystemLegacy: '/system/:systemId',
  SystemDesign: '/systems/:systemId/design',
  SystemCreate: '/systems/create',
  SystemDetails: '/systems/:systemId/details',
  Unauthorized: '/no-access',
  TermsConditions: '/accept-terms-and-conditions',
  Systems: '/systems',
  SystemUsers: '/systems/:systemId/users',
  SystemNotes: '/systems/:systemId/notes',
  SystemAdmin: '/systems/:systemId/admin',
  Users: '/users',
  AddUser: '/users/add',
  EditUser: '/users/edit/:userId',
  BOM: '/systems/:systemId/bill-of-materials',
  Statistics: '/statistics',
  SystemSimulations: '/systems/:systemId/simulations',
  SystemSimulation: '/systems/:systemId/simulations/:simulationId',
  SimulationMetrics: '/systems/:systemId/simulations/:simulationId/metrics',
  DashboardSimulation: '/systems/:systemId/simulations/:simulationId/dashboard',
};

type RouteKeys = keyof typeof createRoutes;
export type RouteValues = (typeof createRoutes)[RouteKeys];

export const createDashboardRoute = (systemId: SystemModel['id']) =>
  createRoutes.Dashboard.replace(':systemId', systemId);

export const createSystemRoute = (systemId: SystemModel['id']) =>
  createRoutes.System.replace(':systemId', systemId);

export const createSystemSimulationRoute = (
  systemId: string,
  simulationId: string,
) =>
  createRoutes.SystemSimulation.replace(':systemId', systemId).replace(
    ':simulationId',
    simulationId,
  );

export const createSystemDesignRoute = (systemId: SystemModel['id']) =>
  createRoutes.SystemDesign.replace(':systemId', systemId);

export const createRelativeProductRoute = (productId: ProductModel['id']) => {
  assertIsNonBlankString(productId);
  return createRoutes.Product.replace(':productId', productId);
};

export const createRelativeReservoirRoute = (
  reservoirId: ReservoirModel['id'],
) => {
  assertIsNonBlankString(reservoirId);
  return createRoutes.Reservoir.replace(':reservoirId', reservoirId);
};

export const createRelativeDrainRoute = (drainId: string) => {
  assertIsNonBlankString(drainId);
  return createRoutes.Drain.replace(':drainId', drainId);
};

export const createDrainDesignRoute = (systemId: string, drainId: string) => {
  assertIsNonBlankString(systemId);
  assertIsNonBlankString(drainId);
  return `${createSystemDesignRoute(systemId)}/${createRelativeDrainRoute(
    drainId,
  )}`;
};

export const createProductDesignRoute = (
  systemId: string,
  productId: string,
) => {
  assertIsNonBlankString(systemId);
  assertIsNonBlankString(productId);
  return `${createSystemDesignRoute(systemId)}/${createRelativeProductRoute(
    productId,
  )}`;
};

export const createRelativeDeviceRoute = (deviceId: string) => {
  assertIsNonBlankString(deviceId);
  return createRoutes.Device.replace(':deviceId', deviceId);
};

export function createSystemProductRoute(systemId: string, productId: string) {
  assertIsNonBlankString(systemId);
  return `${createSystemRoute(systemId)}/${createRelativeProductRoute(
    productId,
  )}`;
}

export function createSystemReservoirRoute(
  systemId: string,
  reservoirId: string,
) {
  assertIsNonBlankString(systemId);
  return `${createSystemRoute(systemId)}/${createRelativeReservoirRoute(
    reservoirId,
  )}`;
}

export function createSystemDrainRoute(systemId: string, drainId: string) {
  assertIsNonBlankString(systemId);
  return `${createSystemRoute(systemId)}/${createRelativeDrainRoute(drainId)}`;
}

export const createDynamicReservoirRoute = ({
  systemId,
  reservoirId,
}: {
  systemId: string;
  reservoirId: string;
}) =>
  `${createSystemReservoirRoute(systemId, reservoirId)}/${
    createRoutes.DynamicInfo
  }`;

export const createAdvancedRoute = ({
  systemId,
  productId,
}: {
  systemId: string;
  productId: string;
}) => `${createSystemProductRoute(systemId, productId)}/advanced`;

export const METRICS_DATA_PARAM_NAME = 'data';

export function createMetricsRoute({
  systemId,
  metrics,
}: {
  systemId: string;
  metrics: Array<{
    productId: string;
    resourceName: string;
    metricType: MetricType;
  }>;
}) {
  assertIsNonBlankString(systemId);

  const data: Array<{
    productId: string;
    resourceName: string;
    metricType?: string;
  }> = metrics.map((metric) => ({
    productId: metric.productId,
    resourceName: metric.resourceName,
    metricType: MetricType[metric.metricType],
  }));

  const lastMetric = data[data.length - 1];
  while (data.length < 3) {
    data.push({
      productId: lastMetric.productId,
      resourceName: lastMetric.resourceName,
    });
  }

  const encoded = encodeURIComponent(JSON.stringify(data));

  return `${createSystemRoute(
    systemId,
  )}/metrics?${METRICS_DATA_PARAM_NAME}=${encoded}`;
}

export const START_SEARCH_PARAM_NAME = 'start';
export const END_SEARCH_PARAM_NAME = 'end';
export function createSimulationMetricsRoute({
  systemId,
  simulationId,
  metrics,
  range,
}: {
  systemId: string;
  simulationId: string;
  metrics: Array<{
    productId: string;
    resourceName: string;
    metricType: MetricType;
  }>;
  range: {
    start: Date;
    end: Date;
  };
}) {
  assertIsNonBlankString(systemId);
  assertIsNonBlankString(simulationId);

  const data: Array<{
    productId: string;
    resourceName: string;
    metricType?: string;
  }> = metrics.map((metric) => ({
    productId: metric.productId,
    resourceName: metric.resourceName,
    metricType: MetricType[metric.metricType],
  }));

  const lastMetric = data[data.length - 1];
  while (data.length < 3) {
    data.push({
      productId: lastMetric.productId,
      resourceName: lastMetric.resourceName,
    });
  }

  const encoded = encodeURIComponent(JSON.stringify(data));
  const baseUrl = `${createSystemSimulationRoute(
    systemId,
    simulationId,
  )}/metrics?${METRICS_DATA_PARAM_NAME}=${encoded}&${START_SEARCH_PARAM_NAME}=${dateToShortIsoString(
    range.start,
  )}&${END_SEARCH_PARAM_NAME}=${dateToShortIsoString(range.end)}`;

  return baseUrl;
}

export const createDownloadsRoute = (systemId: SystemModel['id']) =>
  createRoutes.Downloads.replace(':systemId', systemId);

export const createSystemUsersRoute = (systemId: SystemModel['id']) =>
  createRoutes.SystemUsers.replace(':systemId', systemId);

export const createSystemNotesRoute = (systemId: SystemModel['id']) =>
  createRoutes.SystemNotes.replace(':systemId', systemId);

export const createSystemAdminRoute = (systemId: SystemModel['id']) =>
  createRoutes.SystemAdmin.replace(':systemId', systemId);

export const createEditUserRoute = (userId: string) =>
  createRoutes.EditUser.replace(':userId', userId);

export const createSystemDetailsRoute = (systemId: string) =>
  createRoutes.SystemDetails.replace(':systemId', systemId);

export const createSystemBomRoute = (systemId: string) =>
  createRoutes.BOM.replace(':systemId', systemId);

export const createSystemAdviceReportRoute = (systemId: string) =>
  createRoutes.AdviceReport.replace(':systemId', systemId);

export const createSystemSimulationsRoute = (systemId: string) =>
  createRoutes.SystemSimulations.replace(':systemId', systemId);

export const createDashboardSimulationRoute = (
  systemId: string,
  simulationId: string,
) =>
  createRoutes.DashboardSimulation.replace(':systemId', systemId).replace(
    ':simulationId',
    simulationId,
  );
